import { take, put, select, call, all, race, delay, fork, takeLatest } from "redux-saga/effects";
import { Action } from "redux";

import client from "../../../api/client/SdsWebApiClient";

import { BumpSerie, ImageAndMaskResponse } from "../../../api/generated/models";
import ImageAndMask from "../../models/imageAndMask";

import { extractMaskMetadata, extractMaskData, MaskMetadata } from "../../../utilities/maskUtils";
import loadPngMetadata from "../../../utilities/pngMetaLoader";

import { SET_IMAGE_AND_MASK_URL, SET_MASK_DATA, MASK_LOADED, RESOLUTION_CHANGED } from "../../actions/imageActions";
import {
    CONFIGURATION_LOADED,
    CONFIGURATION_CHANGED,
    SURFACE_PATTERN_CHANGED,
} from "../../actions/configurationActions";

import { SET_IS_LOADING } from "../../actions/sessionActions";

import {
    getSessionId,
    getSeries,
    getModelId,
    getComposedUrl,
    getImageAndMask,
    getMaskElement,
    getAppConfig,
} from "../selectors";

function* fetchImage(): any {
    const series: BumpSerie[] | undefined = yield select(getSeries);
    const modelId = yield select(getModelId);

    const serie = series?.find((s: BumpSerie) => s.modelId === modelId);

    // Avoid error on resize event before getting bumps.
    if (!serie) {
        yield put({ type: SET_IS_LOADING, isLoading: false });
        return;
    }

    const sessionId = yield select(getSessionId);
    const appConfig = yield select(getAppConfig);
    const deviceWidth = window.innerWidth;
    const deviceHeight = window.innerHeight;
    const maximizeWidth = appConfig.style.configurationImage.maximize === "width" && deviceWidth > 720;
    let width = maximizeWidth ? deviceWidth : 0;
    let height = maximizeWidth ? 0 : deviceHeight;
    if (deviceWidth <= 720 && deviceWidth <= deviceHeight) {
        height = Math.ceil(height / 2);
    }
    if (window.devicePixelRatio) {
        width = Math.round(width * window.devicePixelRatio);
        height = Math.round(height * window.devicePixelRatio);
    }
    try {
        const imageAndMaskPaths: ImageAndMaskResponse = yield client.getCurrentConfigImageAndMaskGet(
            sessionId,
            width,
            height,
            serie.id,
            serie.firstFrame,
            1,
            1,
            0,
            0,
            "",
            "",
            "",
            "",
            "jpg",
            false,
            false,
            "#000000",
            false,
            false
        );

        const composedUrl: string = yield select(getComposedUrl);
        const imageAndMask: ImageAndMask = {
            configurationImagePath: composedUrl + imageAndMaskPaths.imagePath,
            maskPath: composedUrl + imageAndMaskPaths.maskPath,
        };

        yield all([
            put({
                type: SET_IMAGE_AND_MASK_URL,
                imageAndMask,
            }),
            put({ type: SET_IS_LOADING, isLoading: false }),
        ]);
    } catch (e) {
        console.error("[fetchImage]", e);
        yield put({ type: SET_IS_LOADING, isLoading: false });
    }
}

export function* getConfigurationImageAndMaskSaga(): any {
    // Request new image immediately on CONFIGURATION_CHANGED, CONFIGURATION_LOADED and SURFACE_PATTERN_CHANGED
    // but debounce on RESOLUTION_CHANGED.
    while (true) {
        const listenActions = [
            CONFIGURATION_CHANGED,
            CONFIGURATION_LOADED,
            RESOLUTION_CHANGED,
            SURFACE_PATTERN_CHANGED,
        ];

        const action: Action<any> = yield take(listenActions);
        if (action.type === RESOLUTION_CHANGED) {
            while (true) {
                const { debounced, latestAction } = yield race({
                    debounced: delay(500),
                    latestAction: take(listenActions),
                });

                if (debounced || latestAction.type !== RESOLUTION_CHANGED) {
                    break;
                }
            }
        }
        yield fork(fetchImage);
    }
}

// Empty grid dispatched on a failed load; matches extractMaskData's success-path return type.
const EMPTY_MASK = new Uint8ClampedArray(0);

export function* handleMaskLoaded(): any {
    try {
        const { maskPath }: { maskPath: string } = yield select(getImageAndMask);
        const mask = yield select(getMaskElement);

        if (!mask) return;

        const [maskData, maskSources]: [any, MaskMetadata] = yield all([
            call(extractMaskData, mask),
            call(loadPngMetadata, maskPath),
        ]);
        const maskMetadata: MaskMetadata = extractMaskMetadata(maskSources);

        yield put({
            type: SET_MASK_DATA,
            maskMetadata,
            maskData,
        });
    } catch (e) {
        // Keep the takeLatest watcher alive: an uncaught throw here (extractMaskData's
        // drawImage/getImageData, a rejected loadPngMetadata, or extractMaskMetadata throwing on a
        // malformed color) would otherwise kill the watcher and silently disable all future mask
        // loads. Clear the grid so a failed load can't leave a stale mask that mismaps clicks.
        console.error("loadMaskDataSaga: mask load failed, clearing mask data", e);
        yield put({
            type: SET_MASK_DATA,
            maskMetadata: {},
            maskData: EMPTY_MASK,
        });
    }
}

export function* loadMaskDataSaga(): any {
    yield takeLatest(MASK_LOADED, handleMaskLoaded);
}
